import { AsyncProgressStatus, ImportTypeConfig } from "domains/stock-transfer-imports/types";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import {
    FileHttpErrorPane,
    FilePreCheckInvalidPane,
    FilePreCheckValidPane,
    ImportResults,
    ImportWarning
} from "domains/stock-transfer-imports/components";
import { FileNameConflictResponse, HttpError } from "components/ServerValidationErrors";
import { useEffect, useState } from "react";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import localisations from "../../../localisation";
import { usePerformStockTransferPreCheck } from "domains/stock-transfer-imports/services/use-stock-transfer-import-api";

type PreCheckFileProps = {
    fieldCompanyId: number;
    preCheckStatus: AsyncProgressStatus;
    handlePreCheckStatusChange: (status: AsyncProgressStatus) => void;
    importFile: File | undefined;
    importTypeConfig: ImportTypeConfig | undefined;
    forceUploadChoice: boolean;
    handleFileNameConflict: (status: AsyncProgressStatus, error: FileNameConflictResponse) => void;
};

export default function PreCheckFileStage(props: PreCheckFileProps) {
    const { heading, startPreCheckButton, blurb } = localisations.stockTransferImport.stages.preCheckFile;
    const [localFile, setLocalFile] = useState<File | undefined>(undefined);
    const { data, error, status } = usePerformStockTransferPreCheck(
        props.fieldCompanyId,
        localFile,
        props.importTypeConfig,
        props.forceUploadChoice
    );
    const { handlePreCheckStatusChange, handleFileNameConflict } = props;

    useEffect(() => {
        if (status !== AsyncProgressStatus.Success) {
            resetFileStateForNextSubmission();
            handleFileNameConflict(status, error as FileNameConflictResponse);
        }
    }, [status, error, handleFileNameConflict]);

    useEffect(() => {
        handlePreCheckStatusChange(status);
    }, [handlePreCheckStatusChange, status]);

    const handleSubmit = async () => {
        setLocalFile(props.importFile);
    };

    const isGeneralError = (error: Error | null) =>
        error && (!(error instanceof HttpError) || (error as HttpError).status >= 500);

    function resetFileStateForNextSubmission() {
        setLocalFile(undefined);
    }

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {heading}
            </Typography>
            <Stack spacing={1}>
                {props.preCheckStatus === AsyncProgressStatus.NotSubmitted && (
                    <>
                        <Typography variant="body1" gutterBottom>
                            {blurb}
                        </Typography>
                        <Button variant="contained" endIcon={<FactCheckIcon />} onClick={handleSubmit}>
                            {startPreCheckButton}
                        </Button>                   
                    </>
                )}
                {props.preCheckStatus === AsyncProgressStatus.InProgress &&                   
                     <>
                        <ImportWarning />
                        <Loading />
                    </>}
                {props.preCheckStatus === AsyncProgressStatus.Success && <FilePreCheckValidPane />}
                {props.preCheckStatus === AsyncProgressStatus.Failed &&
                    (error instanceof HttpError && isGeneralError(error) ? (
                        <FileHttpErrorPane errorMessage={error?.message} />
                    ) : (
                        <FilePreCheckInvalidPane />
                    ))}
                {(props.preCheckStatus === AsyncProgressStatus.Failed ||
                    props.preCheckStatus === AsyncProgressStatus.Success) && (
                    <ImportResults
                        requestErrors={data ? data.requestErrors : (error as any)?.data?.requestErrors}
                        itemErrors={data ? data.requestItemErrors : (error as any)?.data?.requestItemErrors}
                        itemActions={data ? data.requestItemActions : (error as any)?.data?.requestItemActions}
                    />
                )}
            </Stack>
        </>
    );
}

function Loading() {
    return (
        <Stack spacing={5} alignItems="center">
            <CircularProgress aria-busy="true" />
        </Stack>
    );
}
